import type { ReactNode } from 'react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export type UsageColumn<T> = {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: 'left' | 'right';
  headerClassName?: string;
  cellClassName?: string;
  skeletonClassName?: string;
};

type UsageDataTableProps<T> = {
  columns: UsageColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  isLoading?: boolean;
  emptyLabel: string;
  title?: ReactNode;
  description?: ReactNode;
  toolbar?: ReactNode;
  page: number;
  pageCount: number;
  total?: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
};

function alignClass(align: 'left' | 'right' | undefined) {
  return align === 'right' ? 'text-right' : 'text-left';
}

export function UsageDataTable<T>({
  columns,
  rows,
  getRowKey,
  isLoading = false,
  emptyLabel,
  title,
  description,
  toolbar,
  page,
  pageCount,
  total,
  pageSize,
  onPageChange,
}: UsageDataTableProps<T>) {
  const hasHeader = Boolean(title || description || toolbar);
  const canPrevious = page > 1;
  const canNext = page < pageCount;

  const rangeLabel = (() => {
    if (total === undefined || pageSize === undefined) {
      return null;
    }
    if (total === 0) {
      return '0 résultat';
    }
    const start = (page - 1) * pageSize + 1;
    const end = Math.min(total, page * pageSize);
    return `${start}–${end} sur ${total}`;
  })();

  return (
    <div className="rounded-[var(--radius-surface-4xl)] border border-border/70 bg-card">
      {hasHeader ? (
        <div className="border-b border-border/70 bg-[#f4f4f538] p-4 sm:p-5 dark:bg-muted/40">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="space-y-1">
              {title ? <h2 className="text-base font-semibold text-foreground">{title}</h2> : null}
              {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
            </div>
            {toolbar ? <div className="flex flex-wrap items-center gap-2 xl:justify-end">{toolbar}</div> : null}
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-0">
          <thead>
            <tr className="text-sm text-muted-foreground">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn('px-6 py-4 font-medium', alignClass(column.align), column.headerClassName)}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, rowIndex) => (
                <tr key={`usage-skeleton-${rowIndex}`}>
                  {columns.map((column) => (
                    <td key={column.key} className="border-t border-border px-6 py-4 align-middle">
                      <Skeleton
                        className={cn(
                          'h-5 w-28 rounded-full',
                          column.align === 'right' && 'ml-auto',
                          column.skeletonClassName,
                        )}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="border-t border-border px-6 py-10 text-center text-sm text-muted-foreground"
                >
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={getRowKey(row)} className="align-middle transition-colors hover:bg-muted/30">
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        'border-t border-border px-6 py-4 align-middle text-[15px] text-foreground',
                        alignClass(column.align),
                        column.cellClassName,
                      )}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 border-t border-border px-6 py-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
        <p className="text-sm text-muted-foreground">{rangeLabel ?? `Page ${page} sur ${pageCount}`}</p>
        <Pagination className="justify-center">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                aria-disabled={!canPrevious}
                className={cn(!canPrevious && 'pointer-events-none opacity-50')}
                onClick={(event) => {
                  event.preventDefault();
                  if (canPrevious) {
                    onPageChange(page - 1);
                  }
                }}
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#" isActive onClick={(event) => event.preventDefault()}>
                {page}
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                aria-disabled={!canNext}
                className={cn(!canNext && 'pointer-events-none opacity-50')}
                onClick={(event) => {
                  event.preventDefault();
                  if (canNext) {
                    onPageChange(page + 1);
                  }
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
        <div />
      </div>
    </div>
  );
}
